/**
 * Role Mapper
 * Maps between API responses and Domain entities
 *
 * @module domains/personnel/infrastructure/mappers/roleMapper
 */

import { PERMISSIONS } from '@personnel/domain/types/PermissionsConst';
import type { RoleInputDto } from '@personnel/application/dtos';
import type { RoleApiItem, RolesListApiResponse, RoleApiRequest } from '@personnel/infrastructure/api/types';
import type { Role } from '@personnel/domain/entities/Role';
import { createRoleFromData } from '@personnel/domain/entities/Role';
import { createRolePermissions } from '@personnel/domain/value-objects/RolePermissions';

/**
 * Maps a single role from API response to Domain entity
 *
 * Delegates permission construction to the RolePermissions Value Object,
 * then uses the domain factory to create a validated Role entity.
 *
 * @param apiRole - Role data from API (camelCase)
 * @returns Domain Role entity with validation
 * @throws {Error} If API data fails domain validation
 *
 * @example
 * const domainRole = mapApiRoleToDomain(apiRoleData);
 */
export const mapApiRoleToDomain = (apiRole: RoleApiItem): Role => {
  // ✅ Use domain Value Object to construct permissions with business rules
  const permissions = createRolePermissions({
    backendPermissionIds: apiRole.permissions,
    endSessionKitchen: apiRole.roleConfiguration.endSessionKitchen,
  });

  // ✅ Use domain factory for validation
  return createRoleFromData({
    uuid: apiRole.uuid,
    name: apiRole.name,
    description: apiRole.description,
    organizationUuid: apiRole.organizationUuid,
    roleConfiguration: {
      tipRetentionAmount: apiRole.roleConfiguration.tipRetentionAmount,
      endSessionKitchen: apiRole.roleConfiguration.endSessionKitchen,
    },
    permissions,
    usersCount: apiRole.numUsers,
  });
};

/**
 * Maps API response to array of Domain roles
 *
 * @param apiResponse - API response with data array
 * @returns Array of Domain Role entities
 *
 * @example
 * const roles = mapApiRolesListToDomain(apiResponse);
 */
export const mapApiRolesListToDomain = (apiResponse: RolesListApiResponse): Role[] => {
  return apiResponse.data.map(mapApiRoleToDomain);
};

/**
 * Maps Application DTO to API request for role operations (create/update)
 *
 * Special handling: The "end_session_kitchen" permission is NOT sent in the permissions array.
 * Instead, it's sent as a boolean flag at the root level.
 *
 * Used for both POST /api/v2/roles and PATCH /api/v2/roles/{roleUuid}
 *
 * @param dto - Application DTO with role input data
 * @returns API request body
 *
 * @example
 * const request = mapRoleInputDtoToApiRequest({
 *   name: 'Manager',
 *   permissions: ['store-pos-accounts-auto-logout', 'org-menu']
 * });
 * // Returns:
 * // {
 * //   name: 'Manager',
 * //   description: '',
 * //   permissions: ['menu.access'],
 * //   end_session_kitchen: true
 * // }
 */
export const mapRoleInputDtoToApiRequest = (dto: RoleInputDto): RoleApiRequest => {
  // Check if the special "end_session_kitchen" permission is selected
  const hasEndSessionKitchen = dto.permissions.includes('store-pos-accounts-auto-logout');

  // Convert frontend permission IDs to backend permission IDs
  // Filter out the special "end_session_kitchen" permission
  // Send ALL primaryIds (for migration) + ALL dependencyIds
  const backendPermissions = dto.permissions
    .filter((frontendId) => frontendId !== 'store-pos-accounts-auto-logout')
    .flatMap((frontendId) => {
      const permission = PERMISSIONS[frontendId];
      return permission ? [...permission.primaryIds, ...permission.dependencyIds] : [];
    });

  return {
    name: dto.name,
    description: '', // Empty for now, not collected in form
    permissions: backendPermissions,
    end_session_kitchen: hasEndSessionKitchen,
  };
};
